// src/components/Inventory.js
import React, { useState, useEffect } from 'react';
import { db } from '../firebaseConfig';
import {
  collection,
  query,
  where,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  serverTimestamp,
  orderBy,
  increment,
  writeBatch
} from 'firebase/firestore';
import * as XLSX from 'xlsx';
import { useTranslation } from 'react-i18next'; // <-- MODIFICA: Importa hook

// --- IMPORT MUI (Invariati) ---
import {
  Box,
  Typography, 
  IconButton,
  CircularProgress, Paper, Divider, Button, TextField, List,
  ListItem, ListItemText, ListItemIcon, ButtonGroup, Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarehouseIcon from '@mui/icons-material/Warehouse';
import RemoveIcon from '@mui/icons-material/Remove';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import EditIcon from '@mui/icons-material/Edit';

// --- MODIFICA: Modale di Modifica (tradotto) ---
const EditItemModal = ({ open, onClose, item, onSave, categories, suppliers, isLoadingCategories, isLoadingSuppliers }) => {
  const { t } = useTranslation('inventory'); // Hook traduzione
  const [formData, setFormData] = useState({});
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (item) {
      const categoryObj = categories.find(c => c.id === item.categoryId) || null;
      const supplierObj = suppliers.find(s => s.id === item.supplierId) || null;
      setFormData({ ...item, category: categoryObj, supplier: supplierObj });
    }
  }, [item, categories, suppliers]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAutocompleteChange = (name, value) => {
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      console.error("Error updating item:", error);
      alert(t('edit_error')); // Tradotto
    } finally {
      setIsSaving(false);
    }
  };

  if (!item) return null;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>{t('edit_modal_title', { name: item?.name || '' })}</DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {/* RIGA 1: Nome, Codice */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField label={t('item_name')} name="name" value={formData.name || ''} onChange={handleChange} required fullWidth sx={{ flex: 1 }} />
            <TextField label={t('item_code')} name="code" value={formData.code || ''} onChange={handleChange} fullWidth sx={{ flex: 1 }} />
          </Box>
          {/* RIGA 2: Categoria, Fornitore */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <Autocomplete
              options={categories}
              getOptionLabel={(option) => option.name}
              value={formData.category || null}
              onChange={(e, val) => handleAutocompleteChange('category', val)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => ( <TextField {...params} label={t('category')} /> )}
              disabled={isLoadingCategories} 
              fullWidth
              sx={{ flex: 1 }}
            />
            <Autocomplete
              options={suppliers}
              getOptionLabel={(option) => option.companyName}
              value={formData.supplier || null}
              onChange={(e, val) => handleAutocompleteChange('supplier', val)}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              renderInput={(params) => ( <TextField {...params} label={t('supplier')} /> )}
              disabled={isLoadingSuppliers} 
              fullWidth
              sx={{ flex: 1 }}
            />
          </Box>
          {/* RIGA 3: Soglia, Posizione */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField label={t('threshold')} name="lowStockThreshold" type="number" value={formData.lowStockThreshold || 0} onChange={handleChange} required inputProps={{ min: 0 }} sx={{ flex: 1 }} />
            <TextField label={t('location')} name="itemLocation" value={formData.itemLocation || ''} onChange={handleChange} fullWidth sx={{ flex: 1 }} />
          </Box>
          {/* RIGA 4: Prezzi */}
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
            <TextField label={t('price_purchase')} name="prezzoAcquisto" type="number" value={formData.prezzoAcquisto || 0} onChange={handleChange} inputProps={{ step: "0.01", min: "0" }} sx={{ flex: 1 }} helperText={t('price_purchase_helper')} />
            <TextField label={t('price_sales')} name="prezzoVendita" type="number" value={formData.prezzoVendita || 0} onChange={handleChange} inputProps={{ step: "0.01", min: "0" }} sx={{ flex: 1 }} helperText={t('price_sales_helper')} />
          </Box>
          {/* RIGA 5: Note */}
          <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5 }}>
            <TextField label={t('notes')} name="notes" value={formData.notes || ''} onChange={handleChange} fullWidth multiline rows={3} />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button onClick={onClose} disabled={isSaving}>{t('edit_cancel_button')}</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaving}>
          {isSaving ? <CircularProgress size={24} /> : t('edit_save_button')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
// --- FINE MODIFICA ---


function Inventory({ teamId, userRole }) {
  // --- MODIFICA: Inizializza t() ---
  const { t } = useTranslation('inventory');
  // --- FINE MODIFICA ---

  // Stati (Invariati)
  const [inventory, setInventory] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);
  const [filterCategory, setFilterCategory] = useState('all');
  const [suppliers, setSuppliers] = useState([]);
  const [isLoadingSuppliers, setIsLoadingSuppliers] = useState(true);
  const [itemName, setItemName] = useState('');
  const [itemCode, setItemCode] = useState('');
  const [itemQuantity, setItemQuantity] = useState(1);
  const [itemThreshold, setItemThreshold] = useState(1);
  const [itemCategory, setItemCategory] = useState(null);
  const [itemSupplier, setItemSupplier] = useState(null);
  const [itemLocation, setItemLocation] = useState('');
  const [itemNotes, setItemNotes] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false); 
  const [isImporting, setIsImporting] = useState(false); 
  const [importError, setImportError] = useState(''); 
  const [importSuccess, setImportSuccess] = useState(''); 
  const [prezzoAcquisto, setPrezzoAcquisto] = useState('');
  const [prezzoVendita, setPrezzoVendita] = useState('');
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);

  // useEffects (INVARIATI)
  useEffect(() => {
    if (!teamId) { setIsLoading(false); return; }
    setIsLoading(true);
    const q = query(collection(db, "inventory"), where("teamId", "==", teamId), orderBy("name", "asc"));
    const unsub = onSnapshot(q, (qs) => {
      setInventory(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoading(false);
    }, (err) => { console.error("Error loading inventory:", err); setIsLoading(false); });
    return () => unsub();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) { setIsLoadingCategories(false); return; }
    setIsLoadingCategories(true);
    const q = query(collection(db, "inventoryCategories"), where("teamId", "==", teamId), orderBy("name", "asc"));
    const unsub = onSnapshot(q, (qs) => {
      setCategories(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingCategories(false);
    }, (err) => { console.error("Error loading categories:", err); setIsLoadingCategories(false); });
    return () => unsub();
  }, [teamId]);

  useEffect(() => {
    if (!teamId) { setIsLoadingSuppliers(false); return; }
    setIsLoadingSuppliers(true);
    const q = query(collection(db, "suppliers"), where("teamId", "==", teamId), orderBy("companyName", "asc"));
    const unsub = onSnapshot(q, (qs) => {
      setSuppliers(qs.docs.map(d => ({ id: d.id, ...d.data() })));
      setIsLoadingSuppliers(false);
    }, (err) => { console.error("Error loading suppliers:", err); setIsLoadingSuppliers(false); });
    return () => unsub();
  }, [teamId]);

  // handleAddItem (invariato)
  const handleAddItem = async (e) => {
    e.preventDefault();
    const name = itemName.trim();
    const code = itemCode.trim();
    const quantity = Number(itemQuantity);
    const threshold = Number(itemThreshold);
    const location = itemLocation.trim();
    const pAcquisto = Number(prezzoAcquisto) || 0;
    const pVendita = Number(prezzoVendita) || 0;
    
    // Alert non tradotti (validazione interna)
    if (!name || quantity < 0 || threshold < 0) { alert("Valid Name, Quantity, and Threshold (>= 0) required."); return; }
    if (inventory.some(item => item.name.toLowerCase() === name.toLowerCase())) { alert("Item with this name already exists."); return; }
    if (code && inventory.some(item => item.code && item.code.toLowerCase() === code.toLowerCase())) { alert("Item with this code already exists."); return; }

    setIsSubmitting(true);
    try {
      await addDoc(collection(db, "inventory"), {
        teamId: teamId, name: name, code: code, quantity: quantity, lowStockThreshold: threshold,
        notes: itemNotes.trim(), categoryId: itemCategory ? itemCategory.id : null,
        categoryName: itemCategory ? itemCategory.name : null, supplierId: itemSupplier ? itemSupplier.id : null,
        supplierName: itemSupplier ? itemSupplier.companyName : null, itemLocation: location,
        prezzoAcquisto: pAcquisto,
        prezzoVendita: pVendita,
        createdAt: serverTimestamp()
      });
      setItemName(''); setItemCode(''); setItemQuantity(1); setItemThreshold(1); setItemNotes(''); setItemCategory(null); setItemSupplier(null); setItemLocation('');
      setPrezzoAcquisto(''); setPrezzoVendita(''); 
    } catch (error) { console.error("Error adding item:", error); }
    setIsSubmitting(false);
  };
  
  // handleUpdateQuantity (invariato)
  const handleUpdateQuantity = async (itemId, amount) => {
    try { await updateDoc(doc(db, "inventory", itemId), { quantity: increment(amount) }); }
    catch (error) { console.error("Error updating quantity:", error); }
  };
  
  // --- MODIFICA: handleDeleteItem (con t()) ---
  const handleDeleteItem = async (itemId) => {
    if (window.confirm(t('delete_confirm'))) { // Tradotto
        try { await deleteDoc(doc(db, "inventory", itemId)); }
        catch (error) { console.error("Error deleting item:", error); }
    }
  };
  
  // --- MODIFICA: handleFileUpload (con t()) ---
  const handleFileUpload = (e) => {
    const file = e.target.files[0]; if (!file) return; 
    setIsImporting(true); setImportError(''); setImportSuccess(''); 
    const reader = new FileReader(); 
    reader.onload = async (evt) => {
      try {
        const data = evt.target.result; 
        const workbook = XLSX.read(data, { type: 'binary' }); 
        const sheetName = workbook.SheetNames[0]; 
        const worksheet = workbook.Sheets[sheetName]; 
        const json = XLSX.utils.sheet_to_json(worksheet, { header: 1 }); 

        if (json.length <= 1) throw new Error(t('import_error_empty')); // Tradotto
        json.shift(); 
        const itemsToUpload = []; 
        const existingNames = inventory.map(item => item.name.toLowerCase()); 
        const existingCodes = inventory.map(item => item.code?.toLowerCase()).filter(Boolean); 

        for (const row of json) {
          const name = String(row[0] || '').trim(); 
          const quantity = Number(row[1] || 0); 
          const notes = String(row[2] || '').trim(); 
          const code = String(row[3] || '').trim(); 
          const threshold = Number(row[4] || 1); 
          const categoryName = String(row[5] || '').trim(); 
          const supplierName = String(row[6] || '').trim(); 
          const location = String(row[7] || '').trim();
          const pAcquisto = Number(row[8] || 0);
          const pVendita = Number(row[9] || 0);

          if (!name) continue; 
          if (existingNames.includes(name.toLowerCase())) continue; 
          if (code && existingCodes.includes(code.toLowerCase())) continue; 
          if (quantity < 0 || threshold < 0) continue; 

          const foundCategory = categoryName ? categories.find(c => c.name.toLowerCase() === categoryName.toLowerCase()) : null; 
          const foundSupplier = supplierName ? suppliers.find(s => s.companyName.toLowerCase() === supplierName.toLowerCase()) : null; 

          itemsToUpload.push({
            teamId: teamId, name: name, code: code, 
            quantity: quantity, lowStockThreshold: threshold, notes: notes, 
            categoryId: foundCategory ? foundCategory.id : null, 
            categoryName: foundCategory ? foundCategory.name : null, 
            supplierId: foundSupplier ? foundSupplier.id : null, 
            supplierName: foundSupplier ? foundSupplier.companyName : null, 
            itemLocation: location,
            prezzoAcquisto: pAcquisto,
            prezzoVendita: pVendita,
            createdAt: serverTimestamp() 
          });
        }
        
        if (itemsToUpload.length === 0) throw new Error(t('import_error_no_new')); // Tradotto
        const batch = writeBatch(db); 
        const inventoryRef = collection(db, "inventory"); 
        itemsToUpload.forEach((item) => { batch.set(doc(inventoryRef), item); }); 
        await batch.commit(); 
        setImportSuccess(t('import_success', { count: itemsToUpload.length })); // Tradotto
      } catch (err) {
        console.error("Import error:", err);
        setImportError(`Error: ${err.message}`); // Errore già tradotto
      } finally {
        setIsImporting(false); e.target.value = null; 
      }
    };
    reader.readAsBinaryString(file); 
  };
  
  // --- MODIFICA: handleExportToExcel (con t()) ---
  const handleExportToExcel = () => {
    if (isLoading) return;
    
    // Traduce le intestazioni
    const dataToExport = filteredInventory.map(item => ({
      [t('item_name')]: item.name,
      [t('item_code')]: item.code || '',
      [t('quantity')]: item.quantity,
      [t('category')]: item.categoryName || 'N/A',
      [t('supplier')]: item.supplierName || 'N/A',
      [t('location')]: item.itemLocation || 'N/A',
      [t('price_purchase')]: item.prezzoAcquisto || 0,
      [t('price_sales')]: item.prezzoVendita || 0,
      [t('threshold')]: item.lowStockThreshold || 0,
      [t('notes')]: item.notes || ''
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    ws['!cols'] = [
      { wch: 30 }, { wch: 15 }, { wch: 10 }, { wch: 20 }, { wch: 20 },
      { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 10 }, { wch: 30 }
    ];
    
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Inventory");
    XLSX.writeFile(wb, `Inventory_Export_${new Date().toISOString().split('T')[0]}.xlsx`);
  };
  
  // filteredInventory (invariato)
  const filteredInventory = inventory.filter(item => {
    if (filterCategory === 'all') return true; 
    if (filterCategory === 'none') return !item.categoryId; 
    return item.categoryId === filterCategory; 
  });

  const isManager = userRole === 'admin' || userRole === 'Engineer' || userRole === 'Magazziniere';

  // Funzioni modale (invariate)
  const handleOpenEditModal = (item) => {
    setCurrentItem(item);
    setIsEditModalOpen(true);
  };
  const handleCloseEditModal = () => {
    setIsEditModalOpen(false);
    setCurrentItem(null);
  };
  const handleUpdateItem = async (formData) => {
    if (!currentItem) return;
    const { category, supplier, ...data } = formData;
    const dataToSave = {
      ...data,
      categoryId: category ? category.id : null,
      categoryName: category ? category.name : null,
      supplierId: supplier ? supplier.id : null,
      supplierName: supplier ? supplier.companyName : null,
      prezzoAcquisto: Number(data.prezzoAcquisto) || 0,
      prezzoVendita: Number(data.prezzoVendita) || 0,
      lowStockThreshold: Number(data.lowStockThreshold) || 0,
    };
    delete dataToSave.id;
    delete dataToSave.quantity; 
    const itemRef = doc(db, "inventory", currentItem.id);
    await updateDoc(itemRef, dataToSave);
  };


  // --- RENDER (MODIFICATO con t()) ---
  return (
    <>
      <Paper elevation={0} sx={{ p: 3, mb: 3, backgroundColor: 'background.paper', border: '1px solid', borderColor: 'divider' }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700 }}>
          {t('title')}
        </Typography>
      </Paper>

      <Paper 
        elevation={0}
        sx={{ p: { xs: 2, sm: 3 }, border: '1px solid', borderColor: 'divider', backgroundColor: 'background.paper' }}
      >
        
        {isManager && (
          <>
            <Box component="form" onSubmit={handleAddItem} sx={{ mb: 3 }}> 
              <Typography variant="h5" gutterBottom>{t('add_title')}</Typography>
              
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
                <TextField label={t('item_name')} value={itemName} onChange={(e) => setItemName(e.target.value)} required fullWidth sx={{ flex: 1 }} />
                <TextField label={t('item_code')} value={itemCode} onChange={(e) => setItemCode(e.target.value)} fullWidth sx={{ flex: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
                <Autocomplete
                  options={categories}
                  getOptionLabel={(option) => option.name}
                  value={itemCategory}
                  onChange={(event, newValue) => setItemCategory(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => ( <TextField {...params} label={t('category')} /> )}
                  disabled={isLoadingCategories} 
                  fullWidth
                  sx={{ flex: 1 }}
                />
                <Autocomplete
                  options={suppliers}
                  getOptionLabel={(option) => option.companyName}
                  value={itemSupplier}
                  onChange={(event, newValue) => setItemSupplier(newValue)}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => ( <TextField {...params} label={t('supplier')} /> )}
                  disabled={isLoadingSuppliers} 
                  fullWidth
                  sx={{ flex: 1 }}
                />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
                <TextField label={t('quantity')} type="number" value={itemQuantity} onChange={(e) => setItemQuantity(e.target.value)} required inputProps={{ min: 0 }} sx={{ width: { xs: '100%', md: 150 } }} />
                <TextField label={t('threshold')} type="number" value={itemThreshold} onChange={(e) => setItemThreshold(e.target.value)} required inputProps={{ min: 0 }} sx={{ width: { xs: '100%', md: 150 } }} />
                <TextField label={t('location')} value={itemLocation} onChange={(e) => setItemLocation(e.target.value)} fullWidth sx={{ flex: 1 }} />
              </Box>
              <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2.5, mb: 2.5 }}>
                <TextField label={t('price_purchase')} type="number" value={prezzoAcquisto} onChange={(e) => setPrezzoAcquisto(e.target.value)} inputProps={{ step: "0.01", min: "0" }} sx={{ flex: 1 }} helperText={t('price_purchase_helper')} />
                <TextField label={t('price_sales')} type="number" value={prezzoVendita} onChange={(e) => setPrezzoVendita(e.target.value)} inputProps={{ step: "0.01", min: "0" }} sx={{ flex: 1 }} helperText={t('price_sales_helper')} />
              </Box>
              <Box sx={{ display: 'flex', gap: 2.5, mb: 2.5 }}>
                <TextField label={t('notes')} value={itemNotes} onChange={(e) => setItemNotes(e.target.value)} fullWidth multiline rows={2} />
              </Box>
              <Button type="submit" variant="contained" disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} /> : <AddIcon />} sx={{ mt: 1, height: '56px' }} size="large">
                {t('add_button')}
              </Button>
            </Box>
            <Divider sx={{ my: 2 }} />
            
            <Box sx={{ mb: 3 }}>
              <Typography variant="h6" gutterBottom>{t('import_export_title')}</Typography>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button variant="outlined" component="label" startIcon={isImporting ? <CircularProgress size={20} /> : <UploadFileIcon />} disabled={isImporting}>
                  {t('import_button')} <input type="file" hidden onChange={handleFileUpload} accept=".xlsx, .xls, .csv" />
                </Button>
              </Box>
              <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                {t('import_columns_note')}
              </Typography>
              {importSuccess && <Alert severity="success" sx={{ mt: 2 }}>{importSuccess}</Alert>}
              {importError && <Alert severity="error" sx={{ mt: 2 }}>{importError}</Alert>}
            </Box>
            <Divider sx={{ my: 2 }} />
          </>
        )}

        {/* Sezione Inventario Attuale */}
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h5" gutterBottom sx={{ mb: 0 }}>{t('current_title')}</Typography>
            <FormControl sx={{ minWidth: 240 }} disabled={isLoadingCategories}>
              <InputLabel id="filter-cat-label">{t('filter_label')}</InputLabel>
              <Select
                labelId="filter-cat-label" 
                value={filterCategory} 
                label={t('filter_label')}
                onChange={(e) => setFilterCategory(e.target.value)}
              >
                <MenuItem value="all">{t('filter_all')}</MenuItem>
                <MenuItem value="none">{t('filter_none')}</MenuItem>
                {categories.map((cat) => ( <MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem> ))}
              </Select>
            </FormControl>
          </Box>
          
          {isLoading && <CircularProgress size={24} />}
          {!isLoading && inventory.length === 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>{t('empty')}</Typography> )}
          {!isLoading && filteredInventory.length === 0 && inventory.length > 0 && ( <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 2 }}>{t('empty_category')}</Typography> )}
          
          {!isLoading && filteredInventory.length > 0 && (
            <List>
              {filteredInventory.map((item) => (
                <ListItem
                  key={item.id}
                  divider
                  sx={{ 
                    alignItems: 'flex-start',
                    backgroundColor: 'background.default',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    mb: 1,
                    pr: { xs: '120px', sm: '180px', md: '200px' } 
                  }}
                  secondaryAction={ isManager && ( 
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: 'flex-end', gap: 1, pt: 1 }}> 
                      <ButtonGroup size="small" variant="outlined"> 
                        <Button onClick={() => handleUpdateQuantity(item.id, -1)} disabled={item.quantity <= 0}><RemoveIcon /></Button> 
                        <Button disabled sx={{ color: 'text.primary !important', borderColor: 'grey.500 !important' }}>{item.quantity}</Button> 
                        <Button onClick={() => handleUpdateQuantity(item.id, 1)}><AddIcon /></Button> 
                      </ButtonGroup>
                      <Box>
                        <IconButton edge="end" aria-label="edit" onClick={() => handleOpenEditModal(item)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton edge="end" aria-label="delete" onClick={() => handleDeleteItem(item.id)}>
                          <DeleteIcon color="error" />
                        </IconButton>
                      </Box>
                    </Box> 
                  )}
                >
                  <ListItemIcon sx={{ minWidth: 40, pt: 1.5 }}>
                     <WarehouseIcon color={item.quantity <= (item.lowStockThreshold || 0) ? 'error' : 'inherit'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{ fontWeight: 600, fontSize: '1.1rem' }}
                    secondary={
                      <Box>
                        <Typography component="span" variant="body2" color="primary.main" sx={{ display: 'block', fontWeight: 'bold' }}>
                          {t('item_category_prefix')} {item.categoryName || 'N/A'}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.primary" sx={{ display: 'block', fontWeight: 500, mt: 0.5 }}>
                          {t('item_location_prefix')} {item.itemLocation || 'N/A'}
                        </Typography>
                        {item.supplierName && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('item_supplier_prefix')} {item.supplierName}</Typography>}
                        {item.code && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('item_code_prefix')} {item.code}</Typography>}
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>
                          {t('item_price_purchase')}: €{item.prezzoAcquisto || '0.00'} | {t('item_price_sales')}: €{item.prezzoVendita || '0.00'}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block' }}>{t('item_threshold_prefix')} {item.lowStockThreshold || 0}</Typography>
                        {item.notes && <Typography component="span" variant="body2" color="text.secondary" sx={{ display: 'block', fontStyle: 'italic' }}>{t('item_notes_prefix')} {item.notes}</Typography>}
                      </Box>
                    }
                  />
                </ListItem>
              ))}
            </List>
          )}
        </Box>
      </Paper>

      {/* Modale di Modifica (tradotto internamente) */}
      <EditItemModal
        open={isEditModalOpen}
        onClose={handleCloseEditModal}
        item={currentItem}
        onSave={handleUpdateItem}
        categories={categories}
        suppliers={suppliers}
        isLoadingCategories={isLoadingCategories}
        isLoadingSuppliers={isLoadingSuppliers}
      />
    </>
  );
}

export default Inventory;